import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, X, Award, BookOpen, Calendar, Shield, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CertificationVerify() {
  const { hash } = useParams();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hash) { setError('Hash invalide'); setLoading(false); return; }
    axios.get(`${API}/certifications/verify/${hash}`)
      .then(r => setCert(r.data))
      .catch(err => {
        if (err.response?.status === 404) setError('Certification introuvable ou invalide');
        else if (err.response?.status === 400) setError('Hash de vérification invalide');
        else setError('Erreur lors de la vérification');
      })
      .finally(() => setLoading(false));
  }, [hash]);

  const dateStr = cert?.completed_at
    ? new Date(cert.completed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
    : '';

  return (
    <div className="min-h-screen bg-[#0f0e17] flex flex-col">
      <header className="border-b border-[#2a2935] px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="text-[#20c997] font-heading font-bold text-lg">
          Kurso
        </button>
        <span className="text-[#64748b] text-xs flex items-center gap-1.5">
          <Shield size={12} /> Vérification de certification
        </span>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full">
          {loading ? (
            <div className="flex flex-col items-center gap-4">
              <div className="w-10 h-10 border-2 border-[#20c997] border-t-transparent rounded-full animate-spin" />
              <p className="text-[#64748b] text-sm">Vérification en cours...</p>
            </div>
          ) : error ? (
            <div className="bg-[#16161e] border border-[#ff6b6b]/20 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-[#ff6b6b]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <X size={28} className="text-[#ff6b6b]" />
              </div>
              <h1 className="text-white text-xl font-bold mb-2">Certification invalide</h1>
              <p className="text-[#64748b] text-sm">{error}</p>
              <p className="text-[#2a2935] text-xs mt-4">Hash : {hash?.slice(0, 16)}…</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="cert-valid">
              {/* Status badge */}
              <div className="flex items-center justify-center">
                <div className="flex items-center gap-2 bg-[#20c997]/10 border border-[#20c997]/20 rounded-full px-5 py-2">
                  <CheckCircle size={16} className="text-[#20c997]" />
                  <span className="text-[#20c997] text-sm font-bold">Certification valide</span>
                </div>
              </div>

              {/* Carte principale */}
              <div className="bg-[#16161e] border border-[#20c997]/20 rounded-2xl p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-[#ffc107]/10 border border-[#ffc107]/20 flex items-center justify-center shrink-0">
                    <Award size={22} className="text-[#ffc107]" />
                  </div>
                  <div>
                    <h1 className="text-white text-lg font-bold leading-tight">{cert.course_title}</h1>
                    <p className="text-[#64748b] text-sm mt-0.5">Plateforme Kurso LMS</p>
                  </div>
                </div>

                <div className="h-px bg-[#2a2935]" />

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Shield size={14} className="text-[#64748b] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[#64748b] text-xs uppercase tracking-wider font-bold">Titulaire</p>
                      <p className="text-white text-sm font-medium mt-0.5">{cert.user_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <BookOpen size={14} className="text-[#64748b] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[#64748b] text-xs uppercase tracking-wider font-bold">Formation</p>
                      <p className="text-white text-sm mt-0.5">{cert.course_title}</p>
                      {cert.course_description && (
                        <p className="text-[#64748b] text-xs mt-0.5">{cert.course_description}</p>
                      )}
                    </div>
                  </div>
                  {dateStr && (
                    <div className="flex items-start gap-3">
                      <Calendar size={14} className="text-[#64748b] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-[#64748b] text-xs uppercase tracking-wider font-bold">Date d'obtention</p>
                        <p className="text-white text-sm mt-0.5">{dateStr}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="h-px bg-[#2a2935]" />

                <div>
                  <p className="text-[#64748b] text-[10px] uppercase tracking-wider font-bold mb-1">Hash de vérification</p>
                  <p className="text-[#2a2935] text-[10px] font-mono break-all">{hash}</p>
                </div>
              </div>

              <div className="text-center">
                <p className="text-[#64748b] text-xs">
                  Cette certification a été délivrée par{' '}
                  <a href="https://mykurso.fr" className="text-[#20c997] hover:underline">kurso.app</a>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
